import { Component, OnInit, OnDestroy, signal, inject, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TestimonialService, Testimonial } from '../../../services/testimonial';
import { AdminHeader } from '../../../services/admin-header';
import { ModalService } from '../../../services/modal.service';

@Component({
	selector: 'app-testimonials',
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './testimonials.html',
	styleUrl: './testimonials.css',
})
export class Testimonials implements OnInit, OnDestroy {
	private testimonialService = inject(TestimonialService);
	private fb = inject(FormBuilder);
	private adminHeader = inject(AdminHeader);
	private modalService = inject(ModalService);

	@ViewChild('testimonialModalTemplate') testimonialModalTemplate!: TemplateRef<any>;

	testimonials = signal<Testimonial[]>([]);
	isLoading = signal(true);
	isSubmitting = signal(false);
	errorMessage = signal('');
	editingId = signal<number | null>(null);

	testimonialForm: FormGroup = this.fb.group({
		name: ['', [Validators.required, Validators.maxLength(255)]],
		role: ['', [Validators.maxLength(255)]],
		content: ['', Validators.required],
		rating: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
		is_active: [true],
	});

	ngOnInit() {
		this.loadTestimonials();
		this.adminHeader.setHeaderAction({
			label: 'Add Testimonial',
			icon: 'M12 4v16m8-8H4',
			callback: () => this.openModal(),
		});
	}

	ngOnDestroy() {
		this.adminHeader.clearHeaderAction();
		// Close modal if open when navigating away
		this.modalService.close();
	}

	loadTestimonials() {
		this.isLoading.set(true);
		this.testimonialService.getAll().subscribe({
			next: (res) => {
				if (res.status) this.testimonials.set(res.data);
				this.isLoading.set(false);
			},
			error: () => this.isLoading.set(false),
		});
	}

	openModal(testimonial?: Testimonial) {
		if (testimonial) {
			this.editingId.set(testimonial.id);
			this.testimonialForm.patchValue(testimonial);
		} else {
			this.editingId.set(null);
			this.testimonialForm.reset({ rating: 5, is_active: true });
		}
		this.errorMessage.set('');

		// Open modal using modal service
		this.modalService.open(this.testimonialModalTemplate, '672px', () => {
			// onClose callback
			this.testimonialForm.reset();
			this.editingId.set(null);
			this.errorMessage.set('');
		});
	}

	closeModal() {
		this.modalService.close();
	}

	saveTestimonial() {
		if (this.testimonialForm.invalid) return;

		this.isSubmitting.set(true);
		const data = this.testimonialForm.value;
		const action = this.editingId()
			? this.testimonialService.update(this.editingId()!, data)
			: this.testimonialService.create(data);

		action.subscribe({
			next: () => {
				this.loadTestimonials();
				this.closeModal();
				this.isSubmitting.set(false);
			},
			error: (err) => {
				this.errorMessage.set(err.error?.message || 'Failed to save testimonial');
				this.isSubmitting.set(false);
			},
		});
	}

	deleteTestimonial(testimonial: Testimonial) {
		if (!confirm(`Delete testimonial from ${testimonial.name}?`)) return;

		this.testimonialService.delete(testimonial.id).subscribe({
			next: () => this.loadTestimonials(),
			error: (err) => alert(err.error?.message || 'Failed to delete testimonial'),
		});
	}

	toggleActive(testimonial: Testimonial) {
		this.testimonialService.update(testimonial.id, { is_active: !testimonial.is_active }).subscribe({
			next: () => this.loadTestimonials(),
			error: (err) => alert(err.error?.message || 'Failed to update status'),
		});
	}
}
